<template>
  <div class="tasklist__table-panel" data-testid="tasklist__table-panel">
    <div class="lb-datatableaction-wrapper" ref="tableWrapper">
      <lb-datatableaction
              ref="dataTableAction"
              :key="tableColumnLayoutKey"
              :headers="displayHeaders"
              :tabledata="filterByTab"
              :filter-options-data="filterOptionsData"
              :displayedRowCount="paginatedFilteredItems.length"
              :itemsperpage="itemsPerPage"
              :searchterm="search"
              :enableSaveOption="true"
              thead-class="tasklist-thead"
              header-row-class="tasklist-header-row"
              header-cell-class="tasklist-header-cell"
              @select-all-change="$emit('select-all-change', $event)"
              :isDataSelected="isPartialSelection"
              :checkalldata="isAllSelected"
              :select-all-disabled="bulkLocked"
              :column-actions-disabled="bulkLocked"
              @filter-item="$emit('filter-item', $event)"
              @clear-item="$emit('clear-item', $event)"
              @filter-active-change="$emit('filter-active-change', $event)"
              @column-menu-change="onColumnMenuChange"
              @restore-sticky-column-classes="$emit('restore-sticky-column-classes')"
              @refresh="$emit('refresh')"
              @sort-order="onSortOrder"
              @column-reorder="$emit('column-reorder', $event)"
              :selectfilter="tableFilterHeader"
              :page_details="page_details"
              class="tasklist-table"
              data-testid="tasklist__datatable-action"
              header-bg-class="fbc-table-header-bg"
              :sticky-column-count="stickyColumnCount"
            >
              <template v-slot:tbody="{ headers: slotHeaders }">
                <tr
                  v-for="(item, index) in paginatedFilteredItems"
                  :key="item?._id ? `${item._id}-${index}` : `task-${index}`"
                  class="cursor-pointer tasklist__table-row height-60"
                  :class="{
                    'low-opacity': item.status === false,
                    'tasklist__table-row--action-active': isRowActionTarget(item),
                  }"
                  data-testid="tasklist__table-row"
                  :data-task-id="item._id"
                  @mouseenter="onRowMouseEnter($event, item)"
                  @mouseleave="onRowMouseLeave"
                  @focusin="onRowFocusIn($event, item)"
                  @focusout="onRowFocusOut($event)"
                >
                  <td class="align-center justify-center tasklist__table-row-leading-cell">
                    <div class="tasklist__table-row-leading-cell-inner">
                      <v-checkbox
                        dense
                        color="primary"
                        class="ma-0 py-1 tasklist__table-checkbox"
                        :ripple="false"
                        hide-details
                        :input-value="isTaskSelected(item)"
                        @change="onRowSelectChange(item, $event)"
                        data-testid="tasklist__table-row-checkbox"
                      ></v-checkbox>
                      <button
                        v-if="!isTaskSelected(item)"
                        type="button"
                        class="tasklist__row-actions-trigger"
                        :aria-label="getRowActionsAriaLabel(item)"
                        :data-testid="`tasklist__row-actions-trigger-${index}`"
                        @focus="onRowActionsTriggerFocus($event, item)"
                      >
                        <span class="tasklist__row-actions-trigger-label">Actions</span>
                      </button>
                    </div>
                  </td>
                  <td
                    v-for="header in visibleSlotHeaders(slotHeaders)"
                    :key="header.value"
                    class=""
                    :class="[
                      header.value === 'description' ? 'description-header' : 'single-line',
                      header.cellClass,
                    ]"
                    @click="['description','ref','task_type','stage'].includes(header.value) ? $emit('navigate-task', item) : null"
                    data-testid="tasklist__table-cell"
                  >
                      <div class="" v-if="header.value === 'location'">
                        <span>{{ getLocationById(item.location) }} </span>
                      </div>
                      <div
                        v-else-if="header.value === 'description'"
                        class="tasklist__task-name data-table-action__truncatable"
                        :class="{ 'data-table-action__cell--link-hover': isLinkHoverHeader(header) }"
                        data-testid="tasklist__task-name"
                      >{{ getItemData(item, header.value) }}</div>
                      <div
                        v-else-if="header.value === 'duedate1'"
                        class="d-flex align-center"
                      >
                        <TaskDueDateCell
                          :due-date="item.duedate"
                          :due-days="item.duedays"
                          :suppress-urgency="isClosedTask(item)"
                          :data-test-id="`tasklist__due-date-cell-${index}`"
                        />
                      </div>
                      <div class="d-flex" v-else-if="header.value === 'task_type'">
                        <span>{{ getTaskTypeDisplayLabel(item.task_type) }}</span>
                      </div>
                      <div class="d-flex align-center" v-else-if="header.value === 'stage1'">
                        <StatusIndicator
                          v-if="getStageStatusVariant(item)"
                          :variant="getStageStatusVariant(item)"
                          :custom-label="getStageStatusLabel(item)"
                          size="sm"
                          data-testid="tasklist__status-indicator"
                          outlined
                        />
                      </div>
                      <div class="amount-cell" v-else-if="header.value === '__tbtotals'">
                        <span class="amount-value" v-if="isChecklistTask(item)">{{ FINANCIAL_AMOUNT_NA }}</span>
                        <span class="amount-value" v-else-if="(getItemData(item, header.value) || {}).total">
                          {{ formatAmount((getItemData(item, header.value) || {}).total || 0) }}
                        </span>
                        <span class="amount-value" v-else>0</span>
                      </div>
                      <div
                        class="amount-cell amount-cell--unexplained"
                        :class="{
                          'amount-cell--unexplained-warning justify-space-between': shouldShowUnexplainedThresholdWarning(item),
                        }"
                        v-else-if="header.value === 'difference'"
                      >
                        <template v-if="isChecklistTask(item)">
                          <span class="amount-value">{{ FINANCIAL_AMOUNT_NA }}</span>
                        </template>
                        <template v-else>
                          <v-tooltip
                            v-if="shouldShowUnexplainedThresholdWarning(item)"
                            bottom
                            fixed
                            content-class="tooltip-bottom tasklist-unexplained-threshold-tooltip"
                          >
                            <template #activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                v-on="on"
                                class="tasklist__unexplained-threshold-warning"
                                role="img"
                                :aria-label="getUnexplainedThresholdTooltipText(item)"
                                data-testid="tasklist__unexplained-threshold-warning"
                              >
                                <img src="/img/icons/AlertIcon.svg" alt="" aria-hidden="true" />
                              </span>
                            </template>
                            <span>{{ getUnexplainedThresholdTooltipText(item) }}</span>
                          </v-tooltip>
                          <span class="amount-value" data-testid="tasklist__unexplained-difference-value">
                            {{ formatAmount(calculateUnexplainedDifference(item)) }}
                          </span>
                        </template>
                      </div>
                      <div class="amount-cell" v-else-if="header.value === 'scheduleAmount'">
                        <span class="amount-value">
                          {{ isChecklistTask(item) ? FINANCIAL_AMOUNT_NA : formatAmount(calculateScheduleAmount(item)) }}
                        </span>
                      </div>
                      <div class="amount-cell" v-else-if="header.value === 'explainedDifference'">
                        <span class="amount-value">
                          {{ isChecklistTask(item) ? FINANCIAL_AMOUNT_NA : formatAmount(calculateExplainedDifference(item)) }}
                        </span>
                      </div>
                      <div class="" v-else-if="header.value === 'preparerids'">
                        <div class="d-flex px-1" v-if="item?.preparerids && item.preparerids.length > 0">
                          <OverflowList
                            :items="item.preparerids"
                            :visible-count="2"
                            position="top"
                            fixed
                            content-class="tasklist-preparer-fixed-tooltip"
                            data-testid="tasklist__preparer-more-avatar"
                          >
                            <template #primary="{ item: preparer }">
                              <FcTooltip
                                v-if="preparer"
                                placement="top"
                                eager
                                transition="fade-transition"
                                :open-delay="0"
                                :text="preparer"
                              >
                                <v-avatar
                                  size="24"
                                  color="avatar-bg"
                                  class="white--text font-small d-flex pa-0 ma-0 noTransform"
                                  data-testid="tasklist__preparer-avatar"
                                >
                                  <span class="tasklist__preparer-avatar-text">{{ preparer.charAt(0) }}</span>
                                </v-avatar>
                              </FcTooltip>
                            </template>
                          </OverflowList>
                        </div>
                      </div>
                      <div class="" v-else-if="header.value === 'approverids'">
                        <TaskApproverCell
                          :task="item"
                          :user-map="userMap"
                        />
                      </div>
                      <div class="" v-else-if="header.value === 'created_at'">
                        {{ $nova.formatDate(item.created_at) }}
                      </div>
                      <div class="d-flex gap-2" v-else-if="header.value === 'tags'">
                          <v-chip
                            v-for="(item, index) in item.tags"
                            :key="index"
                            label
                            color="lightpurple"
                            class="caccounting--text"
                            data-testid="tasklist__tag"
                            ><span>{{ item }}</span></v-chip
                          >
                      </div>
                      <div
                        v-else-if="header.value === 'assertions'"
                        class="d-flex"
                        data-testid="tasklist__assertions-cell"
                      >
                        <OverflowList
                          :items="assertionItems(item)"
                          position="top"
                          fixed
                          data-testid="tasklist__assertions-overflow"
                        />
                      </div>
                      <div class="" v-else-if="header.value === 'statusdetail'">
                        <v-tooltip bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              class="shadow-off"
                              data-testid="tasklist__status-button"
                            >
                              <v-icon :color="getStatusConfig(item.statusdetail).color">
                                {{ getStatusConfig(item.statusdetail).icon }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ getStatusConfig(item.statusdetail).text }}</span>
                        </v-tooltip>
                      </div>
                      <div v-else-if="header.value === 'ref'" class="d-flex align-center gap-3"> 
                        <span>{{ getItemData(item, header.value) }}</span>
                        <img
                          v-if="isTaskBotLinked(item)"
                          src="/img/icons/fbc-icons/AskNoa.svg"
                            alt="bot-logo"
                          data-testid="task-ref__bot-icon"
                          />
                      </div>
                      <div v-else-if="header.value === 'priority'" class="d-flex align-center">
                        <TaskPriorityDisplay
                          :priority="item.priority"
                          :data-test-id="`tasklist__priority-cell-${index}`"
                        />
                      </div>
                    <span v-else>{{ getItemData(item, header.value) }}</span>
                  </td>
                </tr>
              </template>
      </lb-datatableaction>

      <div
        v-show="showHoverActionsOverlay"
        ref="hoverOverlay"
        class="tasklist-hover-actions-overlay"
        role="toolbar"
        :aria-label="activeHoverTask ? getRowActionsAriaLabel(activeHoverTask) : 'Row actions'"
        :style="hoverOverlayStyle"
        @mouseenter="onOverlayMouseEnter"
        @mouseleave="onOverlayMouseLeave"
        @focusin="onOverlayFocusIn"
        @focusout="onOverlayFocusOut"
      >
        <TaskRowHoverActions
          v-if="activeHoverTask"
          :task="activeHoverTask"
          :actions="getRowHoverActionsForTask(activeHoverTask)"
          @action="onRowHoverAction($event, activeHoverTask)"
        >
          <template #more>
            <TasklistRowActionMenu
              :task="activeHoverTask"
              :user-type="userType"
              :metadata="metadata"
              :userid="userid"
              :is-open="openActionMenuTaskId === activeHoverTask._id"
              @toggle="onActionMenuToggle(activeHoverTask._id, $event)"
              @delete-checklist-item="$emit('delete-checklist-item', $event)"
              @workpaper-template="$emit('workpaper-template')"
              @open-helpdesk="$emit('open-helpdesk', $event)"
              @open-link-bot="$emit('open-link-bot', $event)"
              @unlink-bot="$emit('unlink-bot', $event)"
              @view-task-details="$emit('view-task-details', $event)"
              @edit-task="$emit('edit-task', $event)"
              @open-activity-log="openTaskActivityLog"
              @open-comments="openTaskComments"
            />
          </template>
        </TaskRowHoverActions>
      </div>
    </div>
    <activity-log
      hide-trigger
      ref="taskActivityLog"
      :resource-id="taskActivityLogId"
      :row-description="taskActivityLogDescription"
      data-testid="tasklist__activity-log-modal"
    />
    <lb-comment
      ref="taskComments"
      hide-trigger
      v-model="taskCommentsDrawerOpen"
      heading="Conversation"
      width="600"
      :recurring="false"
      :recurringText="''"
      :closed="taskConversationClosed"
      :showname="true"
      @markread="onMarkTaskConversationRead(taskCommentsTask)"
      :notify="taskCommentsTask && !taskCommentsTask.conversation_read"
      :getapi="taskConversationGetApi"
      :sendapi="taskConversationSendApi"
      :readapi="taskConversationReadApi"
      :name="'Comment'"
      :mentionapi="'/v2/financialclose/analysis/getusersformdata'"
      data-testid="tasklist__comment"
    />
  </div>
</template>

<script>
import './TasklistTable.scss';
import ActivityLog from '../../../components/ActivityLog/ActivityLog.vue';
import { TaskDueDateCell, TaskPriorityDisplay, TaskApproverCell } from './components';
import TaskRowHoverActions from './components/TaskRowHoverActions/TaskRowHoverActions.vue';
import TasklistRowActionMenu from './components/TasklistRowActionMenu/TasklistRowActionMenu.vue';
import {
  calculateUnexplainedDifference,
  calculateScheduleAmount,
  calculateExplainedDifference,
  isTaskBotLinked,
  getStageDisplay,
} from '../../../utils/utils';
import {
  getRowHoverActions,
  ROW_HOVER_ACTION_IDS,
  isChecklistTask,
  FINANCIAL_AMOUNT_NA,
  getTaskStageDisplayLabel,
  getTaskStageStatusVariant,
  isTasklistColumnVisible,
  formatTasklistYesNoDisplayValue,
  normalizeTasklistStringList,
} from '../../../utils/tasklistHelpers';
import {
  TASK_STAGES,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
} from '../../../utils/constants';
import StatusIndicator from '@/components/StatusIndicator.vue';
import {
  TASK_FINANCIAL_METRIC_TEXT,
  buildUnexplainedExceedsThresholdTooltip,
} from '../../../utils/listConstants';
import { DEFAULT_TASKLIST_ITEMS_PER_PAGE } from '../../../utils/tasklistConstants';
import { OverflowList } from '@/modules/financialclose/components';
import FcTooltip from '@/modules/financialclose/components/FcTooltip/FcTooltip.vue';

export default {
  name: 'TasklistTable',
  components: {
    ActivityLog,
    TaskPriorityDisplay,
    TaskDueDateCell,
    TaskRowHoverActions,
    TasklistRowActionMenu,
    StatusIndicator,
    TaskApproverCell,
    OverflowList,
    FcTooltip,
  },
  props: {
    tableHeadersfilter: { type: Array, default: () => [] },
    filterByTab: { type: Array, default: () => [] },
    /** Pre-column-filter rows for stable ColumnFilter option lists (task_type etc.). */
    filterOptionsData: { type: Array, default: () => [] },
    paginatedFilteredItems: { type: Array, default: () => [] },
    itemsPerPage: { type: Number, default: DEFAULT_TASKLIST_ITEMS_PER_PAGE },
    search: { type: String, default: '' },
    tableFilterHeader: { type: Object, default: () => ({}) },
    page_details: { type: Object, default: () => ({}) },
    selectedTasks: { type: Array, default: () => [] },
    userType: { type: String, default: 'preparer' },
    metadata: { type: Object, default: () => ({}) },
    userid: { type: String, default: '' },
    right: { type: String, default: '' },
    fileNamesList: { type: Array, default: () => [] },
    locationList: { type: Array, default: () => [] },
    userMap: { type: Object, default: () => ({}) },
    isPartialSelection: { type: Boolean, default: false },
    isAllSelected: { type: Boolean, default: false },
    stickyColumnCount: { type: Number, default: 3 },
    /** Rows still loading: select-all and column filter menus are disabled. */
    bulkLocked: { type: Boolean, default: false },
  },
  data() {
    return {
      FINANCIAL_AMOUNT_NA,
      openActionMenuTaskId: null,
      taskActivityLogId: '',
      taskActivityLogDescription: '',
      taskCommentsDrawerOpen: false,
      taskCommentsTask: null,
      hoveredTask: null,
      hoveredRowEl: null,
      hoverOverlayStyle: {},
      overlayHoverLock: false,
      tableScrollEl: null,
      hoverOverlayRafId: null,
    };
  },
  computed: {
    displayHeaders() {
      // Drop Action columns (upload + legacy more-action); leave other visible:false
      // headers intact so DataTableAction can still manage column toggles/filters.
      return (this.tableHeadersfilter || []).filter(
        (header) => header?.value !== 'action' && header?.value !== 'upload'
      );
    },
    tableColumnLayoutKey() {
      return (this.displayHeaders || [])
        .map((header) => `${header.value}:${isTasklistColumnVisible(header) ? 1 : 0}`)
        .join('|');
    },
    activeHoverTask() {
      if (this.openActionMenuTaskId) {
        return (
          this.paginatedFilteredItems.find((task) => task?._id === this.openActionMenuTaskId)
          || this.hoveredTask
        );
      }
      return this.hoveredTask;
    },
    showHoverActionsOverlay() {
      const task = this.activeHoverTask;
      return Boolean(task && !this.isTaskSelected(task));
    },
    taskConversationClosed() {
      if (this.userType === 'preparer') return false;
      return this.metadata?.stage !== 0;
    },
    taskConversationGetApi() {
      const taskId = this.taskCommentsTask?._id;
      const projectId = this.metadata?._id;
      if (!taskId || !projectId) return '';
      return `/v2/financialclose/analysis/getconversation/${projectId}/c/${taskId}`;
    },
    taskConversationSendApi() {
      const taskId = this.taskCommentsTask?._id;
      const projectId = this.metadata?._id;
      if (!taskId || !projectId) return '';
      return `/v2/financialclose/analysis/addconversation/${projectId}/c/${taskId}`;
    },
    taskConversationReadApi() {
      const taskId = this.taskCommentsTask?._id;
      const projectId = this.metadata?._id;
      if (!taskId || !projectId) return '';
      return `/v2/financialclose/analysis/markreadconversation/${projectId}/c/${taskId}`;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.bindTableScrollListener();
    });
  },
  beforeDestroy() {
    this.cancelHoverOverlayPositionUpdate();
    this.unbindTableScrollListener();
  },
  methods: {
    onColumnMenuChange(isOpen) {
      this.$emit('column-menu-change', isOpen);
    },
    visibleSlotHeaders(headers = []) {
      return (headers || []).filter((header) => isTasklistColumnVisible(header));
    },
    getRowHoverActionsForTask(task) {
      return getRowHoverActions(this.userType, task, {
        userid: this.userid,
        metadata: this.metadata,
      });
    },
    calculateUnexplainedDifference,
    calculateScheduleAmount,
    calculateExplainedDifference,
    isTaskBotLinked,
    getStageDisplay,
    isChecklistTask,
    isClosedTask(item) {
      return item?.stage === TASK_STAGES.CLOSED;
    },
    getItemData(item, key) {
      return formatTasklistYesNoDisplayValue(key, item?.[key]);
    },
    assertionItems(item) {
      return normalizeTasklistStringList(item?.assertions);
    },
    /** Stored "Normal Task" → user-facing "Checklist". */
    getTaskTypeDisplayLabel(taskType) {
      if (taskType == null || taskType === '') return '—';
      if (taskType === LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME) {
        return LEGACY_TASK_TYPE_LABEL_CHECKLIST;
      }
      return taskType;
    },
    /** linkHover header prop, or Task Name column (saved layouts may omit the prop). */
    isLinkHoverHeader(header) {
      return Boolean(header?.linkHover) || header?.value === 'description';
    },
    getLocationById(id) {
      const location = this.locationList?.find((loc) => loc._id === id);
      return location?.name || '';
    },
    getStageStatusVariant(task) {
      return getTaskStageStatusVariant(task);
    },
    getStageStatusLabel(task) {
      // StatusIndicator default for fc-pending-approval is "Pending Approval"
      if (task?.stage === 1) return 'Processing';
      return getTaskStageDisplayLabel(task);
    },
    getStatusConfig(statusDetail) {
      const constants = this.$constants.TASK_LIST_CONSTANTS;
      const statusConfig = {
        [constants.STATUS_DETAILS.PENDING_SUBMISSION_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_SUBMISSION,
          color: constants.STATUS_COLORS.SUBMISSION,
          text: constants.STATUS_DETAILS.PENDING_SUBMISSION_DUE,
        },
        [constants.STATUS_DETAILS.PENDING_SUBMISSION_NOT_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_SUBMISSION_NOT_DUE,
          color: constants.STATUS_COLORS.SUBMISSION,
          text: constants.STATUS_DETAILS.PENDING_SUBMISSION_NOT_DUE,
        },
        [constants.STATUS_DETAILS.PENDING_SUBMISSION_OVERDUE]: {
          icon: constants.STATUS_ICONS.PENDING_SUBMISSION_OVERDUE,
          color: constants.STATUS_COLORS.SUBMISSION,
          text: constants.STATUS_DETAILS.PENDING_SUBMISSION_OVERDUE,
        },
        [constants.STATUS_DETAILS.PENDING_APPROVAL_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_APPROVAL,
          color: constants.STATUS_COLORS.APPROVAL,
          text: constants.STATUS_DETAILS.PENDING_APPROVAL_DUE,
        },
        [constants.STATUS_DETAILS.PENDING_APPROVAL_NOT_DUE]: {
          icon: constants.STATUS_ICONS.PENDING_APPROVAL_NOT_DUE,
          color: constants.STATUS_COLORS.APPROVAL,
          text: constants.STATUS_DETAILS.PENDING_APPROVAL_NOT_DUE,
        },
        [constants.STATUS_DETAILS.PENDING_APPROVAL_OVERDUE]: {
          icon: constants.STATUS_ICONS.PENDING_APPROVAL_OVERDUE,
          color: constants.STATUS_COLORS.APPROVAL,
          text: constants.STATUS_DETAILS.PENDING_APPROVAL_OVERDUE,
        },
      };
      return statusConfig[statusDetail] || {
        icon: constants.STATUS_ICONS.CLOSED,
        color: constants.STATUS_COLORS.CLOSED,
        text: constants.STATUS_DETAILS.CLOSED_TASK,
      };
    },
    formatAmount(amount) {
      return this.$nova.formatNumber((Number(amount) || 0).toFixed(0));
    },
    shouldShowUnexplainedThresholdWarning(item) {
      if (isChecklistTask(item)) return false;

      const unexplained = Number(calculateUnexplainedDifference(item));
      if (!unexplained || unexplained === 0) return false;

      const threshold = Number(item?.threshold ?? 0);
      if (Number.isNaN(threshold)) return false;

      return Math.abs(unexplained) > threshold;
    },
    getTaskThresholdDisplay(item) {
      const value = item?.threshold;
      if (value === null || value === undefined || value === '') {
        return TASK_FINANCIAL_METRIC_TEXT.NOT_AVAILABLE;
      }
      return this.formatAmount(value);
    },
    getUnexplainedThresholdTooltipText(item) {
      return buildUnexplainedExceedsThresholdTooltip(this.getTaskThresholdDisplay(item));
    },
    isTaskSelected(item) {
      return this.selectedTasks.some((t) => t?._id === item?._id);
    },
    onRowSelectChange(item, selected) {
      let next = [...this.selectedTasks];
      const idx = next.findIndex((t) => t?._id === item?._id);
      if (selected && idx === -1) {
        next.push(item);
      } else if (!selected && idx !== -1) {
        next.splice(idx, 1);
      }
      this.$emit('update:selected-tasks', next);
    },
    onSortOrder(key, order) {
      this.$emit('sort-order', key, order);
    },
    onActionMenuToggle(taskId, isOpen) {
      this.openActionMenuTaskId = isOpen ? taskId : null;
      if (isOpen) {
        const task = this.paginatedFilteredItems.find((t) => t?._id === taskId);
        if (task) {
          this.hoveredTask = task;
        }
        this.$nextTick(() => {
          if (!this.hoveredRowEl && taskId) {
            this.hoveredRowEl = this.$el.querySelector(
              `[data-testid="tasklist__table-row"][data-task-id="${taskId}"]`
            );
          }
          this.updateHoverOverlayPosition();
        });
        return;
      }
      if (!this.overlayHoverLock) {
        this.clearHoverState();
      }
    },
    isRowActionTarget(item) {
      return (
        this.hoveredTask?._id === item?._id
        || this.openActionMenuTaskId === item?._id
      );
    },
    getTableScrollWrapper() {
      return this.$refs.tableWrapper?.querySelector('.v-data-table__wrapper') || null;
    },
    bindTableScrollListener() {
      this.tableScrollEl = this.getTableScrollWrapper();
      if (!this.tableScrollEl) return;
      this.tableScrollEl.addEventListener('scroll', this.onTableViewportChange, { passive: true });
      window.addEventListener('resize', this.onTableViewportChange, { passive: true });
      window.addEventListener('scroll', this.onTableViewportChange, { passive: true });
    },
    unbindTableScrollListener() {
      this.cancelHoverOverlayPositionUpdate();
      if (this.tableScrollEl) {
        this.tableScrollEl.removeEventListener('scroll', this.onTableViewportChange);
      }
      window.removeEventListener('resize', this.onTableViewportChange);
      window.removeEventListener('scroll', this.onTableViewportChange);
    },
    cancelHoverOverlayPositionUpdate() {
      if (this.hoverOverlayRafId != null) {
        cancelAnimationFrame(this.hoverOverlayRafId);
        this.hoverOverlayRafId = null;
      }
    },
    onTableViewportChange() {
      if (!this.showHoverActionsOverlay || this.hoverOverlayRafId != null) return;

      this.hoverOverlayRafId = requestAnimationFrame(() => {
        this.hoverOverlayRafId = null;
        if (this.showHoverActionsOverlay) {
          this.updateHoverOverlayPosition();
        }
      });
    },
    onRowMouseEnter(event, item) {
      this.activateRowActions(event.currentTarget, item);
    },
    onRowMouseLeave(event) {
      this.deactivateRowActions(event, event.currentTarget);
    },
    onRowFocusIn(event, item) {
      if (event.target.closest('.tasklist__row-actions-trigger')) return;
      this.activateRowActions(event.currentTarget, item);
    },
    onRowFocusOut(event) {
      this.deactivateRowActions(event, event.currentTarget);
    },
    onRowActionsTriggerFocus(event, item) {
      const rowEl = event.currentTarget.closest('tr');
      this.activateRowActions(rowEl, item);
      this.focusFirstOverlayAction();
    },
    activateRowActions(rowEl, item) {
      if (!rowEl || this.isTaskSelected(item)) return;

      if (this.openActionMenuTaskId && this.openActionMenuTaskId !== item._id) {
        this.openActionMenuTaskId = null;
      }

      this.hoveredTask = item;
      this.hoveredRowEl = rowEl;
      this.updateHoverOverlayPosition();
    },
    shouldKeepRowActionsOpen(relatedTarget, rowEl) {
      if (!relatedTarget) return false;
      if (this.$refs.hoverOverlay?.contains(relatedTarget)) return true;
      if (rowEl?.contains(relatedTarget)) return true;
      return false;
    },
    deactivateRowActions(event, rowEl) {
      if (this.openActionMenuTaskId || this.overlayHoverLock) return;

      const related = event?.relatedTarget;
      if (this.shouldKeepRowActionsOpen(related, rowEl)) return;

      this.clearHoverState();
    },
    focusFirstOverlayAction() {
      this.$nextTick(() => {
        const overlay = this.$refs.hoverOverlay;
        if (!overlay) return;

        const firstFocusable = overlay.querySelector(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      });
    },
    getRowActionsAriaLabel(item) {
      const name = (item?.description || '').trim() || 'task';
      return `Row actions for ${name}`;
    },
    onOverlayMouseEnter() {
      this.overlayHoverLock = true;
    },
    onOverlayMouseLeave(event) {
      this.overlayHoverLock = false;
      this.deactivateRowActions(event, this.hoveredRowEl);
    },
    onOverlayFocusIn() {
      this.overlayHoverLock = true;
    },
    onOverlayFocusOut(event) {
      this.overlayHoverLock = false;
      this.deactivateRowActions(event, this.hoveredRowEl);
    },
    clearHoverState() {
      this.hoveredTask = null;
      this.hoveredRowEl = null;
      this.hoverOverlayStyle = {};
    },
    updateHoverOverlayPosition() {
      let rowEl = this.hoveredRowEl;
      if (!rowEl && this.activeHoverTask?._id) {
        rowEl = this.$el.querySelector(
          `[data-testid="tasklist__table-row"][data-task-id="${this.activeHoverTask._id}"]`
        );
      }

      const wrapper = this.getTableScrollWrapper();
      if (!rowEl || !wrapper) {
        this.openActionMenuTaskId = null;
        this.overlayHoverLock = false;
        this.clearHoverState();
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();

      // Hide when the active row is fully outside the table viewport so clamped
      // positioning cannot leave actions available for an off-screen task.
      const isOutOfView =
        rowRect.bottom <= wrapperRect.top
        || rowRect.top >= wrapperRect.bottom
        || rowRect.right <= wrapperRect.left
        || rowRect.left >= wrapperRect.right;

      if (isOutOfView) {
        this.openActionMenuTaskId = null;
        this.overlayHoverLock = false;
        this.clearHoverState();
        return;
      }

      const overlayHeight = this.$refs.hoverOverlay?.offsetHeight || 40;
      const edgeOffset = 12;
      const minTop = wrapperRect.top + 4;
      const maxTop = wrapperRect.bottom - overlayHeight - 4;
      const idealTop = rowRect.top + (rowRect.height - overlayHeight) / 2;
      const top = Math.max(minTop, Math.min(idealTop, maxTop));

      this.hoverOverlayStyle = {
        top: `${top}px`,
        left: `${wrapperRect.right - edgeOffset}px`,
      };
    },
    onRowHoverAction({ actionId }, item) {
      switch (actionId) {
        case ROW_HOVER_ACTION_IDS.LINK_NOA:
          this.$emit('open-link-bot', item);
          break;
        case ROW_HOVER_ACTION_IDS.EDIT_PREPARER:
          this.$emit('row-edit-preparer', item);
          break;
        case ROW_HOVER_ACTION_IDS.EDIT_APPROVER:
          this.$emit('row-edit-approver', item);
          break;
        case ROW_HOVER_ACTION_IDS.UPLOAD:
          this.$emit('open-upload-drawer', item);
          break;
        case ROW_HOVER_ACTION_IDS.DOWNLOAD:
          this.$emit('download-single-task-files', item);
          break;
        case ROW_HOVER_ACTION_IDS.APPROVE:
          this.$emit('open-approve-reject', { type: 'approve', task: item });
          break;
        case ROW_HOVER_ACTION_IDS.REJECT:
        case ROW_HOVER_ACTION_IDS.RETURN_TO_PENDING:
          this.$emit('open-approve-reject', { type: 'reject', task: item });
          break;
        case ROW_HOVER_ACTION_IDS.VIEW_DETAILS:
          this.$emit('view-task-details', item);
          break;
        default:
          break;
      }
    },
    openTaskActivityLog(item) {
      if (!item?._id) return;
      this.openActionMenuTaskId = null;
      this.taskActivityLogId = item._id;
      this.taskActivityLogDescription = item.description || '';
      this.$nextTick(() => {
        this.$refs.taskActivityLog?.openDialog();
      });
    },
    openTaskComments(item) {
      if (!item?._id || !this.metadata?._id) return;
      this.openActionMenuTaskId = null;
      this.taskCommentsTask = item;
      this.$nextTick(() => {
        this.taskCommentsDrawerOpen = true;
        this.$refs.taskComments?.openConversation();
      });
    },
    onMarkTaskConversationRead(task) {
      const targetId = task?._id || this.taskCommentsTask?._id;
      const row = (this.paginatedFilteredItems || []).find((t) => t?._id === targetId);
      if (row) row.conversation_read = true;
      if (this.taskCommentsTask?._id === targetId) {
        this.taskCommentsTask.conversation_read = true;
      }
    },
  },
};

</script>
